import {
  Button,
  Field,
  Input,
  InputNumber,
  Select,
  TextArea,
} from '@/components/commons';
import { AyumiService } from '@/services/ayumi';
import { IResponseError } from '@/types/common';
import {
  AYUMI_PRIORITY_OPTIONS,
  AYUMI_SCALE_OPTIONS,
  AYUMI_SERVICE_TYPES_WITH_OFFER,
  AYUMI_SERVICE_TYPE_LABELS,
  AYUMI_SERVICE_TYPE_ORDER,
  AyumiLevel,
  AyumiServiceType,
} from '@/types/common';
import { ISaveAyumiCategoryRequest } from '@/types/request';
import { IAyumiCategoryResponse } from '@/types/response';
import {
  NOTIFY_STATUS,
  notificationMessage,
  notificationModal,
} from '@/utils';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef } from 'react';
import { useFieldArray, useForm, FormProvider } from 'react-hook-form';
import { useMutation } from 'react-query';
import { categorySchemaFn } from '../formSchemaFn';
import { MAX_LENGTH } from '../constants';

interface IProps {
  topicId: number;
  /** 新規カード（未保存）は undefined */
  category?: IAyumiCategoryResponse;
  offerOptions: { value: number; label: string }[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: -1 | 1) => void;
  onSaved: () => void;
  onCancelNew?: () => void;
  /** 未保存かどうかを親（アクションカードの警告）に伝える */
  onDirtyChange?: (isDirty: boolean) => void;
}

const emptyValues = {
  name: '',
  priority: AyumiLevel.MIDDLE,
  impact: AyumiLevel.MIDDLE,
  frequency: AyumiLevel.MIDDLE,
  score: 0,
  items: [] as ISaveAyumiCategoryRequest['items'],
};

export const CategoryCard: React.FC<IProps> = ({
  topicId,
  category,
  offerOptions,
  canMoveUp,
  canMoveDown,
  onMove,
  onSaved,
  onCancelNew,
  onDirtyChange,
}) => {
  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(categorySchemaFn()),
    defaultValues: category
      ? {
          name: category.name,
          priority: category.priority,
          impact: category.impact,
          frequency: category.frequency,
          score: category.score,
          items: category.items?.map((item) => ({
            id: item.id,
            serviceType: item.serviceType,
            title: item.title,
            description: item.description ?? '',
            offerId: item.offerId ?? null,
          })),
        }
      : emptyValues,
  });

  const { control, handleSubmit, reset, formState } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const { isDirty } = formState;

  // カードは1枚ずつ独立したフォームなので、未保存であることは親に自動では伝わらない。
  // onDirtyChange は毎レンダー作り直される props なので、依存に入れず ref 経由で呼ぶ
  // （依存に入れると「親の state 更新 → 再レンダー → effect 再実行」で無限ループになる）
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);

  // 削除・保存でカードが消えたときに未保存フラグを残さない
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  const { mutate: save, isLoading } = useMutation(
    (body: ISaveAyumiCategoryRequest) =>
      category
        ? AyumiService.updateCategory(body, topicId, category.id)
        : AyumiService.createCategory(body, topicId),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'カテゴリを保存しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        onSaved();
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: 'カテゴリの保存に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
        console.error(error);
      },
    },
  );

  const { mutate: remove_, isLoading: isDeleting } = useMutation(
    () => AyumiService.deleteCategory(topicId, category!.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'カテゴリを削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        onSaved();
      },
      onError: () => {
        notificationMessage({
          message: 'カテゴリの削除に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const onSubmit = (values: any) => {
    save(
      {
        name: values.name,
        priority: Number(values.priority),
        impact: Number(values.impact),
        frequency: Number(values.frequency),
        score: Number(values.score),
        // 区分ごとの表示順は配列の並びで決まるため、区分単位でまとめてから送る
        items: AYUMI_SERVICE_TYPE_ORDER.flatMap((serviceType) =>
          (values.items ?? [])
            .filter((item: any) => Number(item.serviceType) === serviceType)
            .map((item: any) => ({
              ...(item.id ? { id: item.id } : {}),
              serviceType,
              title: item.title,
              description: item.description || undefined,
              offerId: AYUMI_SERVICE_TYPES_WITH_OFFER.includes(serviceType)
                ? item.offerId ?? null
                : null,
            })),
        ),
      },
      // 既存カードは保存しても再マウントしないため、送った値を新しい既定値にして
      // 「未保存」表示を落とす
      { onSuccess: () => reset(values) },
    );
  };

  return (
    <FormProvider {...methods}>
      <div className='flex flex-col gap-3 p-4 rounded border bg-[#f7f7f7]'>
        <div className='flex items-center justify-end gap-2'>
          {isDirty && (
            <span className='mr-auto text-[12px] font-bold text-[#d46b08]'>
              未保存の変更があります
            </span>
          )}
          <Button
            icon={<ArrowUpOutlined />}
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
          />
          <Button
            icon={<ArrowDownOutlined />}
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
          />
          {/* 保存されるのはタブ全体ではなくこのカード1枚だけ */}
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            このカテゴリを保存
          </Button>
          {category ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={isDeleting}
              onClick={() =>
                notificationModal({
                  type: NOTIFY_STATUS.CONFIRM,
                  title: '確認',
                  content:
                    'このカテゴリと配下の対策項目を削除します。よろしいですか？',
                  maskClosable: false,
                  onOk: () => remove_(),
                })
              }
            >
              カテゴリを削除
            </Button>
          ) : (
            <Button onClick={onCancelNew}>キャンセル</Button>
          )}
        </div>

        <Field label='カテゴリ名' name='name' required>
          <Input
            placeholder='例）預貯金の引き出し不能'
            maxLength={MAX_LENGTH.CATEGORY_NAME}
          />
        </Field>

        <div className='flex flex-wrap gap-4'>
          <Field label='優先度' name='priority' required>
            <Select className='w-[140px]' options={AYUMI_PRIORITY_OPTIONS} />
          </Field>
          <Field label='影響度' name='impact' required>
            <Select className='w-[140px]' options={AYUMI_SCALE_OPTIONS} />
          </Field>
          <Field label='頻度' name='frequency' required>
            <Select className='w-[140px]' options={AYUMI_SCALE_OPTIONS} />
          </Field>
          <Field label='獲得点数' name='score' required>
            <InputNumber className='w-[110px]' min={0} placeholder='0' />
          </Field>
        </div>

        {AYUMI_SERVICE_TYPE_ORDER.map((serviceType) => {
          const indexes = fields
            .map((field, index) => ({ field, index }))
            .filter(
              ({ index }) =>
                Number(methods.watch(`items.${index}.serviceType`)) ===
                serviceType,
            );

          return (
            <div key={serviceType} className='flex flex-col gap-2'>
              <span className='font-semibold text-[13px]'>
                ■ {AYUMI_SERVICE_TYPE_LABELS[serviceType]}
              </span>

              {indexes.map(({ field, index }) => (
                <div
                  key={field.id}
                  className='flex flex-col gap-2 p-3 bg-white rounded border'
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-[11px] text-[#888]'>項目</span>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(index)}
                    >
                      削除
                    </Button>
                  </div>

                  <Field label='タイトル' name={`items.${index}.title`} required>
                    <Input
                      placeholder='例）任意後見制度の契約を締結'
                      maxLength={MAX_LENGTH.ITEM_TITLE}
                    />
                  </Field>

                  <Field label='説明文' name={`items.${index}.description`}>
                    <TextArea placeholder='説明文を入力' rows={3} />
                  </Field>

                  {AYUMI_SERVICE_TYPES_WITH_OFFER.includes(serviceType) && (
                    <Field
                      label='紐づけるオファー'
                      name={`items.${index}.offerId`}
                    >
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp='label'
                        placeholder='オファーを選択'
                        options={offerOptions}
                      />
                    </Field>
                  )}
                </div>
              ))}

              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  append({
                    serviceType,
                    title: '',
                    description: '',
                    offerId: null,
                  })
                }
              >
                項目を追加（{AYUMI_SERVICE_TYPE_LABELS[serviceType]}）
              </Button>
            </div>
          );
        })}
      </div>
    </FormProvider>
  );
};
